import { useState } from "react";
import { studentLogin } from "@/api/studentAuth";

export default function StudentLogin() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      await studentLogin({ email, password });
      if (rememberMe) {
        localStorage.setItem('student_remember_email', email);
      } else {
        localStorage.removeItem('student_remember_email');
      }
      window.location.href = "/student";
    } catch {
      setError("بيانات الدخول غير صحيحة");
    } finally {
      setLoading(false);
    }
  };

  // تحميل البريد المحفوظ
  useState(() => {
    const savedEmail = localStorage.getItem('student_remember_email');
    if (savedEmail) {
      setEmail(savedEmail);
      setRememberMe(true);
    }
  });

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <div style={styles.header}>
          <div style={styles.logoContainer}>
            <div style={styles.logoIcon}>👨‍🎓</div>
            <h1 style={styles.systemName}>منصة التعلم للطلاب</h1>
          </div>
          <h2 style={styles.title}>تسجيل دخول الطالب</h2>
          <p style={styles.subtitle}>أدخل بياناتك للوصول إلى الدورات التعليمية</p>
        </div>

        <form onSubmit={submit} style={styles.form}>
          <div style={styles.inputGroup}>
            <label htmlFor="student-email" style={styles.label}>البريد الإلكتروني</label>
            <div style={styles.inputWithIcon}>
              <span style={styles.inputIcon}>📧</span>
              <input
                id="student-email"
                type="email"
                placeholder="student@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                style={styles.input}
                dir="ltr"
              />
            </div>
          </div>

          <div style={styles.inputGroup}>
            <label htmlFor="student-password" style={styles.label}>كلمة المرور</label>
            <div style={styles.inputWithIcon}>
              <span style={styles.inputIcon}>🔒</span>
              <input
                id="student-password"
                type="password"
                placeholder="أدخل كلمة المرور"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                style={styles.input}
              />
            </div>
          </div>

          <div style={styles.formOptions}>
            <label style={styles.rememberMe}>
              <input
                type="checkbox"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
                style={styles.checkbox}
              />
              <span>تذكرني</span>
            </label>
            <a href="/student/forgot-password" style={styles.forgotPassword}>
              نسيت كلمة المرور؟
            </a>
          </div>

          {error && (
            <div style={styles.errorMessage}>
              <span style={styles.errorIcon}>⚠️</span>
              {error}
            </div>
          )}

          <button 
            type="submit" 
            style={loading ? {...styles.submitButton, ...styles.submitButtonLoading} : styles.submitButton}
            disabled={loading}
          >
            {loading ? (
              <>
                <span style={styles.spinner}></span>
                جاري الدخول...
              </>
            ) : (
              "🚀 دخول إلى المنصة"
            )}
          </button>

          <div style={styles.divider}>
            <span>أو</span>
          </div>

          <div style={styles.additionalOptions}>
            <p style={styles.registerLink}>
              ليس لديك حساب؟ <a href="/student/register" style={styles.registerLinkA}>سجّل كطالب جديد</a>
            </p>
            <a href="/" style={styles.backToHome}>
              ← العودة إلى الصفحة الرئيسية
            </a>
          </div>
        </form>

        <div style={styles.footer}>
          <p style={styles.footerText}>© 2024 منصة التعلم. جميع الحقوق محفوظة.</p>
          <div style={styles.footerLinks}>
            <a href="/privacy" style={styles.footerLink}>سياسة الخصوصية</a>
            <a href="/terms" style={styles.footerLink}>الشروط والأحكام</a>
            <a href="/support" style={styles.footerLink}>الدعم الطلابي</a>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
        
        @keyframes pulse {
          0% { transform: scale(1); }
          50% { transform: scale(1.05); }
          100% { transform: scale(1); }
        }
      `}</style>
    </div>
  );
}

const styles = {
  container: {
    minHeight: '100vh',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    background: 'linear-gradient(135deg, #4CAF50 0%, #2E7D32 100%)',
    padding: '20px',
    fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
    direction: 'rtl',
  },
  card: {
    background: 'white',
    borderRadius: '20px',
    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
    width: '100%',
    maxWidth: '450px',
    overflow: 'hidden',
    animation: 'slideUp 0.6s ease-out',
  },
  header: {
    background: 'linear-gradient(135deg, #4CAF50 0%, #2E7D32 100%)',
    color: 'white',
    padding: '40px 30px',
    textAlign: 'center',
  },
  logoContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '15px',
    marginBottom: '20px',
  },
  logoIcon: {
    fontSize: '48px',
    background: 'rgba(255, 255, 255, 0.2)',
    width: '80px',
    height: '80px',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backdropFilter: 'blur(10px)',
  },
  systemName: {
    fontSize: '1.5rem',
    margin: '0',
    fontWeight: '600',
  },
  title: {
    fontSize: '1.8rem',
    margin: '10px 0',
    fontWeight: '700',
  },
  subtitle: {
    fontSize: '0.9rem',
    opacity: '0.9',
    marginTop: '5px',
  },
  form: {
    padding: '40px',
  },
  inputGroup: {
    marginBottom: '25px',
  },
  label: {
    display: 'block',
    marginBottom: '8px',
    fontWeight: '600',
    color: '#333',
    fontSize: '0.9rem',
  },
  inputWithIcon: {
    position: 'relative',
  },
  inputIcon: {
    position: 'absolute',
    right: '15px',
    top: '50%',
    transform: 'translateY(-50%)',
    color: '#4CAF50',
    fontSize: '1.2rem',
  },
  input: {
    width: '100%',
    padding: '15px 50px 15px 20px',
    border: '2px solid #e0e0e0',
    borderRadius: '10px',
    fontSize: '1rem',
    transition: 'all 0.3s ease',
    boxSizing: 'border-box',
  },
  formOptions: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '25px',
  },
  rememberMe: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    cursor: 'pointer',
    color: '#555',
    fontSize: '0.9rem',
  },
  checkbox: {
    width: '18px',
    height: '18px',
    accentColor: '#4CAF50',
  },
  forgotPassword: {
    color: '#4CAF50',
    textDecoration: 'none',
    fontSize: '0.9rem',
    fontWeight: '500',
    transition: 'color 0.3s ease',
  },
  errorMessage: {
    background: '#ffebee',
    color: '#c33',
    padding: '12px 15px',
    borderRadius: '8px',
    marginBottom: '20px',
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    fontSize: '0.9rem',
    borderRight: '4px solid #c33',
  },
  errorIcon: {
    fontSize: '1.2rem',
  },
  submitButton: {
    width: '100%',
    padding: '16px',
    background: 'linear-gradient(135deg, #4CAF50 0%, #2E7D32 100%)',
    color: 'white',
    border: 'none',
    borderRadius: '10px',
    fontSize: '1.1rem',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: '10px',
    marginBottom: '20px',
  },
  submitButtonLoading: {
    opacity: '0.8',
    cursor: 'not-allowed',
  },
  spinner: {
    width: '20px',
    height: '20px',
    border: '3px solid rgba(255, 255, 255, 0.3)',
    borderRadius: '50%',
    borderTopColor: 'white',
    animation: 'spin 1s ease-in-out infinite',
  },
  divider: {
    display: 'flex',
    alignItems: 'center',
    margin: '25px 0',
    color: '#777',
  },
  additionalOptions: {
    textAlign: 'center',
  },
  registerLink: {
    color: '#555',
    marginBottom: '15px',
    fontSize: '0.95rem',
  },
  registerLinkA: {
    color: '#4CAF50',
    textDecoration: 'none',
    fontWeight: '600',
    transition: 'color 0.3s ease',
  },
  backToHome: {
    display: 'inline-block',
    color: '#777',
    textDecoration: 'none',
    fontSize: '0.9rem',
    transition: 'color 0.3s ease',
  },
  footer: {
    background: '#f8f9fa',
    padding: '20px 30px',
    textAlign: 'center',
    borderTop: '1px solid #e0e0e0',
  },
  footerText: {
    color: '#666',
    fontSize: '0.8rem',
    margin: '0 0 10px 0',
  },
  footerLinks: {
    display: 'flex',
    justifyContent: 'center',
    gap: '20px',
  },
  footerLink: {
    color: '#4CAF50',
    textDecoration: 'none',
    fontSize: '0.8rem',
    transition: 'color 0.3s ease',
  },
};

// إضافة تأثيرات Hover
if (typeof window !== 'undefined') {
  setTimeout(() => {
    const style = document.createElement('style');
    style.textContent = `
      .student-input:focus {
        outline: none;
        border-color: #4CAF50 !important;
        box-shadow: 0 0 0 3px rgba(76, 175, 80, 0.1) !important;
      }
      
      .student-forgot-password:hover {
        color: #2E7D32 !important;
        text-decoration: underline !important;
      }
      
      .student-submit-btn:hover:not(:disabled) {
        transform: translateY(-2px) !important;
        box-shadow: 0 10px 20px rgba(76, 175, 80, 0.3) !important;
        animation: pulse 0.5s ease-in-out !important;
      }
      
      .student-register-link:hover {
        color: #2E7D32 !important;
        text-decoration: underline !important;
      }
      
      .student-back-home:hover {
        color: #4CAF50 !important;
      }
      
      .student-footer-link:hover {
        color: #2E7D32 !important;
        text-decoration: underline !important;
      }
      
      .student-logo-icon {
        transition: transform 0.3s ease;
      }
      
      .student-logo-icon:hover {
        transform: rotate(15deg) scale(1.1);
      }
      
      @media (max-width: 480px) {
        .student-card-responsive {
          border-radius: 15px !important;
        }
        
        .student-form-responsive {
          padding: 30px 20px !important;
        }
        
        .student-header-responsive {
          padding: 30px 20px !important;
        }
        
        .student-system-name-responsive {
          font-size: 1.3rem !important;
        }
        
        .student-title-responsive {
          font-size: 1.5rem !important;
        }
        
        .student-form-options-responsive {
          flex-direction: column !important;
          gap: 15px !important;
          align-items: flex-start !important;
        }
        
        .student-footer-links-responsive {
          flex-direction: column !important;
          gap: 10px !important;
        }
      }
    `;
    document.head.appendChild(style);
    
    // إضافة classes للعناصر
    setTimeout(() => {
      document.querySelectorAll('[style*="input"]').forEach(el => {
        el.className = (el.className || '') + ' student-input';
      });
      document.querySelectorAll('[style*="forgotPassword"]').forEach(el => {
        el.className = (el.className || '') + ' student-forgot-password';
      });
      document.querySelectorAll('[style*="submitButton"]').forEach(el => {
        el.className = (el.className || '') + ' student-submit-btn';
      });
      document.querySelectorAll('[style*="registerLinkA"]').forEach(el => {
        el.className = (el.className || '') + ' student-register-link';
      });
      document.querySelectorAll('[style*="backToHome"]').forEach(el => {
        el.className = (el.className || '') + ' student-back-home';
      });
      document.querySelectorAll('[style*="footerLink"]').forEach(el => {
        el.className = (el.className || '') + ' student-footer-link';
      });
      document.querySelectorAll('[style*="logoIcon"]').forEach(el => {
        el.className = (el.className || '') + ' student-logo-icon';
      });
      
      // Responsive classes
      if (window.innerWidth <= 480) {
        const card = document.querySelector('[style*="card"]');
        const form = document.querySelector('[style*="form"]');
        const header = document.querySelector('[style*="header"]');
        const systemName = document.querySelector('[style*="systemName"]');
        const title = document.querySelector('[style*="title"]');
        const formOptions = document.querySelector('[style*="formOptions"]');
        const footerLinks = document.querySelector('[style*="footerLinks"]');
        
        if (card) card.className = (card.className || '') + ' student-card-responsive';
        if (form) form.className = (form.className || '') + ' student-form-responsive';
        if (header) header.className = (header.className || '') + ' student-header-responsive';
        if (systemName) systemName.className = (systemName.className || '') + ' student-system-name-responsive';
        if (title) title.className = (title.className || '') + ' student-title-responsive';
        if (formOptions) formOptions.className = (formOptions.className || '') + ' student-form-options-responsive';
        if (footerLinks) footerLinks.className = (footerLinks.className || '') + ' student-footer-links-responsive';
      }
    }, 200);
  }, 100);
}